'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { Loader } from '@/components/layout/loading';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { LoginPage } from './loginPage';

import { LoginV2DomainPage } from '../login-v2/loginV2DomainPage';
import { LoginV2Page } from '../login-v2/loginV2Page';

// Host-derived facts the flag branches need, computed server-side in page.tsx
// (the LaunchDarkly flag is only available client-side, so the v1/v2 decision
// can't happen on the server — but the host header and auth cookie can't be
// read on the client, so they're passed down).
interface LoginSwitchProps {
  // host ends with `.<knownPublicDomain>` — the tenant-subdomain case the v1
  // flow redirects to the gateway login for.
  isSuffixPublicDomain: boolean;
  // host is exactly a known public domain — the v2 flow shows the domain page.
  isExactPublicDomain: boolean;
  hasAuthToken: boolean;
  hasError: boolean;
}

export function LoginSwitch(props: LoginSwitchProps) {
  const flags = useFeatureFlags();
  // Default to v1 until the flag definitively resolves to `true`. While the
  // anonymous LD client is still initializing (or if it never does), the flag
  // is absent and v1 renders exactly as it did before this flag existed — so
  // login never blocks on, or breaks with, LaunchDarkly availability.
  const loginV2Enabled = flags.LoginV2Enabled === true;

  return loginV2Enabled ? (
    <LoginV2Branch
      isExactPublicDomain={props.isExactPublicDomain}
      hasAuthToken={props.hasAuthToken}
    />
  ) : (
    <LoginV1Branch
      isSuffixPublicDomain={props.isSuffixPublicDomain}
      hasError={props.hasError}
    />
  );
}

// Mirrors the previous server logic of /login (login/page.tsx): tenant
// subdomains are redirected to the gateway login, everything else renders the
// domain-entry form.
function LoginV1Branch({
  isSuffixPublicDomain,
  hasError,
}: Pick<LoginSwitchProps, 'isSuffixPublicDomain' | 'hasError'>) {
  const { clientApiBaseUrl, isDev } = useEnvironment();
  const searchParams = useSearchParams();
  const shouldRedirect = isSuffixPublicDomain && !hasError;

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }
    let url = `${clientApiBaseUrl}/login`;
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl && !isDev) {
      url += `?returnUrl=${returnUrl}`;
    }
    window.location.replace(url);
  }, [shouldRedirect, clientApiBaseUrl, isDev, searchParams]);

  if (shouldRedirect) {
    return <Loader />;
  }
  return <LoginPage />;
}

// Mirrors the server logic of /login-v2 (login-v2/page.tsx), minus the
// demo short-circuit which stays in page.tsx.
function LoginV2Branch({
  isExactPublicDomain,
  hasAuthToken,
}: Pick<LoginSwitchProps, 'isExactPublicDomain' | 'hasAuthToken'>) {
  const { isDev } = useEnvironment();
  const searchParams = useSearchParams();
  const router = useRouter();
  const hasDomain = Boolean(searchParams.get('domain'));
  const redirectHome = !isExactPublicDomain && !isDev && hasAuthToken;

  useEffect(() => {
    if (redirectHome) {
      router.replace('/home');
    }
  }, [redirectHome, router]);

  if (isExactPublicDomain) {
    return <LoginV2DomainPage />;
  }
  if (isDev) {
    return hasDomain ? <LoginV2Page /> : <LoginV2DomainPage />;
  }
  if (redirectHome) {
    return <Loader />;
  }
  return <LoginV2Page />;
}
